import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import { graphql, PageProps, Slice } from 'gatsby';
import {
  AirtableStatus,
  IS_PRO,
  REGION,
  RegionEnum,
  SHOW_MTO_PROGRAM_PDF_FOR_FAMILIES,
} from 'gatsby-theme-hem-store/src/constants';
import { useCustomer } from 'gatsby-theme-hem-store/src/hooks/useCustomer';
import { useInventory } from 'gatsby-theme-hem-store/src/hooks/useInventory';
import { useIsHemX } from 'gatsby-theme-hem-store/src/hooks/useIsHemX';
import { useShowPreOrderVariants } from 'gatsby-theme-hem-store/src/hooks/useShowPreOrderVariants';
import { Breadcrumbs, Button, LoadingIndicator, TextVariant } from 'hem-ui';
import first from 'lodash/first';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { InfoIcon, Paperclip } from 'lucide-react';
import React, { Suspense, useEffect, useState } from 'react';

import { ArticlePreview } from '@/components/ArticlePreview';
import { BackInStock } from '@/components/BackInStock';
import { ExclVat } from '@/components/ExclVat';
import { Info } from '@/components/Info';
import { MarketingCommunicationForms } from '@/components/MarketingCommunicationForms';
import { Modal } from '@/components/Modal';
import { Money } from '@/components/Money';
import { SectionTitle } from '@/components/SectionTitle';
import { SEO } from '@/components/SEO';
import { VariantAvailability } from '@/components/VariantAvailability';
import { cn } from '@/utils';
import { getInventoryQuantity } from '@/utils/getInventoryQuantity';
import { sendGtmProductDetailEvent } from '@/utils/sendGtmEvents';

import ReviewsContainer from '../../components/Reviews/container';
import { AddToCart } from './components/AddToCart';
import { Affirm } from './components/Affirm';
import { AvailableForPreOrderBanner } from './components/AvailableForPreOrderBanner';
import { Description } from './components/Description';
import { LifestyleGallery } from './components/LifestyleGallery';
import { Material } from './components/Material';
import { PreOrderLoading } from './components/PreOrderLoading';
import { PreOrderLogIn } from './components/PreOrderLogIn';
import { ProductRecommendations } from './components/ProductRecommendations';
import ProListPrice from './components/ProListPrice';
import { TechnicalDetails } from './components/TechnicalDetails';
import { VariantCarousel } from './components/VariantCarousel';
import { getBreadcrumbs } from './utils/getBreadcrumbs';

const CylindoViewer = React.lazy(() => import('@/components/CylindoViewer'));
const CustomOptions = React.lazy(() => import('./components/CustomOptions'));
const SelectTexture = React.lazy(() => import('./components/SelectTexture'));
const TotoConfiguration = React.lazy(
  () => import('./components/TotoConfiguration'),
);
const TouchwoodWoodPush = React.lazy(
  () => import('./components/TouchwoodWoodPush'),
);

const VariantPage: React.FC<{
  data: Queries.VariantPageQuery;
}> = ({ data: { variant, reviews } }): JSX.Element => {
  const customer = useCustomer((state) => state.customer);
  const { isHemX } = useIsHemX(variant.slug);
  const [isTextured, setIsTextured] = useState(false);

  useEffect(() => {
    // poll window.dataLayer[] untill event: 'pageview' with same url is present
    const interval = setInterval(() => {
      if (
        window.dataLayer &&
        window.dataLayer.find(
          (e: { event?: string; originalLocation?: string }) => {
            return (
              e.event === 'pageview' &&
              e.originalLocation === window.location.href
            );
          },
        )
      ) {
        sendGtmProductDetailEvent({
          sku: variant.sku,
          slug: variant.slug,
          productName: variant.productName,
          variantName: variant.variantName,
          image: variant.thumbnailImage?.url,
          price: variant.shopify.price + '',
          originalPrice:
            (variant.shopify.compareAtPrice || variant.shopify.price) + '',
        });

        clearInterval(interval);
      }
    }, 100);
  }, [
    variant.productName,
    variant.shopify.compareAtPrice,
    variant.shopify.price,
    variant.sku,
    variant.slug,
    variant.variantName,
    variant.thumbnailImage?.url,
  ]);

  useEffect(() => {
    if (variant.productName === 'Hide') {
      const isTextured = variant.airtable.primaryMaterial?.texture === 'Fine';
      setIsTextured(isTextured);
    }
  }, [variant.airtable.primaryMaterial?.texture, variant.productName]);

  const bothTextures =
    variant.productName === 'Hide'
      ? variant.primaryOptions.filter((option) => {
        let aTitle = option.airtable.primaryMaterial?.finishTitle;
        let bTitle = variant.airtable.primaryMaterial?.finishTitle;

        if (aTitle === 'Signal Black') {
          aTitle = 'Jet Black';
        }

        if (bTitle === 'Signal Black') {
          bTitle = 'Jet Black';
        }

        return aTitle === bTitle;
      })
      : undefined;

  const variantPrimaryOptions = variant.primaryOptions.filter(
    (option) => !!option.airtable?.primaryMaterial,
  );

  const variantPrimaryCustomOptions = variant.primaryCustomOptions.filter(
    (option) => !!option.airtable?.primaryMaterial,
  );

  const variantSecondaryOptions = variant.secondaryOptions.filter(
    (option) => !!option.airtable?.secondaryMaterial,
  );

  const variantConfigurationOptions = variant.configurationOptions.filter(
    (option) => !!option.airtable?.product?.configuration,
  );

  const useInventoryData = useInventory({
    sku: variant.sku,
    skus: uniq(
      [
        { sku: variant.sku },
        ...variantPrimaryOptions.map((v) => ({ sku: v.sku })),
        ...variantPrimaryCustomOptions.map((v) => ({ sku: v.sku })),
        ...variantSecondaryOptions.map((v) => ({ sku: v.sku })),
        ...variantConfigurationOptions.map((v) => ({ sku: v.sku })),
      ]
        .map((v) => v.sku)
        .sort(),
    ),
  });

  const inventoryQuantity = getInventoryQuantity({
    useInventoryData,
    defaultQuantity:
      (variant.airtable.type || '').toLowerCase() === 'custom'
        ? 50
        : variant.shopify.inventoryQuantity,
  });

  const isPreOrder = variant.airtable.status === AirtableStatus.PRE_ORDER;

  let sellToCountry = [];
  switch (REGION) {
    case RegionEnum.Us:
      sellToCountry = ['US'];
      break;
    case RegionEnum.Eu:
      sellToCountry = ['SE', 'IE'];
      break;
    case RegionEnum.Gb:
      sellToCountry = ['GB'];
      break;
    case RegionEnum.Ca:
      sellToCountry = ['CA'];
      break;
  }
  const pairsWith = (variant.pairsWith || [])
    .filter(
      (pairWith) =>
        pairWith.slug &&
        pairWith.productName &&
        pairWith.airtable &&
        pairWith.image,
    )
    .filter((pairWith) =>
      pairWith.airtable.sellToCountries.some((country) =>
        sellToCountry.includes(country),
      ),
    );

  return (
    <>
      <Breadcrumbs crumbs={getBreadcrumbs(variant)} />

      {isPreOrder && (
        <AvailableForPreOrderBanner
          approxLaunchDate={variant.airtable.approxLaunchDate}
        />
      )}

      <div className="mb-16 ml-4 mr-4 grid gap-2 md:ml-0 md:mr-8 md:grid-cols-5 md:gap-4">
        <div className="col-span-3 -mx-4 md:mx-0">
          {variant.cylindo ? (
            <React.Suspense fallback={<div className="aspect-[4/3]" />}>
              <CylindoViewer
                sku={variant.sku}
                code={variant.cylindoCode}
                primaryMaterial={variant.airtable.primaryMaterial?.cylindoId}
                secondaryMaterial={
                  variant.airtable.secondaryMaterial?.cylindoId
                }
                productName={variant.productName}
                variantName={variant.variantName}
              />
            </React.Suspense>
          ) : (
            <VariantCarousel variant={variant} isHemX={isHemX} isSticky />
          )}
        </div>

        <div className="col-span-3 md:col-span-2">
          <h1 className="mb-2 mt-4 text-center text-2xl font-bold text-black md:text-left md:text-3xl">
            {variant.productName}
          </h1>
          <h2 className="mb-4 text-center text-base font-normal md:text-left">
            {variant.variantName}, Art. no. {variant.sku}
          </h2>

          {['variant', 'custom'].includes(
            (variant.airtable.type || '').toLowerCase(),
          ) && (
              <ReviewsContainer
                reviews={reviews.nodes}
                title={variant.productName}
                configuration={variant.airtable.product.configuration}
                placement="summary"
              />
            )}

          <Description variant={variant} />

          <div
            className={cn('mb-4 text-center text-xl font-bold md:text-left', {
              'text-red-gloss':
                variant.shopify.compareAtPrice > variant.shopify.price,
            })}
          >
            {isPreOrder ? (
              <>
                <p className="mt-2 text-base font-normal leading-none text-black-600 md:w-auto md:justify-start">
                  List price
                </p>
                <Money
                  amount={variant.shopify.price}
                  withDiscount={false}
                  sku={variant.sku}
                />
                <ExclVat />
              </>
            ) : (
              <>
                <Money amount={variant.shopify.price} sku={variant.sku} />
                {variant.shopify.compareAtPrice > variant.shopify.price && (
                  <span className="ml-2 font-normal text-black-600 line-through">
                    <Money
                      amount={variant.shopify.compareAtPrice}
                      sku={variant.sku}
                    />
                  </span>
                )}

                <ExclVat />

                {IS_PRO && !!customer && (
                  <ProListPrice listPrice={variant.shopify.price} />
                )}
              </>
            )}
            {variant.airtable.mtoMoq &&
              typeof variant.airtable.mtoMoq === 'number' && (
                <div className="text-sm font-normal text-black-600">
                  This item has a minimum order quantity of{' '}
                  {variant.airtable.mtoMoq}.
                </div>
              )}
          </div>

          <Affirm price={variant.shopify.price} />

          {/* {variant.shopify.product.status === 'DRAFT' && (
            <Info
              type="warning"
              title="Shopify"
              content={`The Shopify status is: ${variant.shopify.product.status}`}
            />
          )} */}

          {inventoryQuantity > 0 && !isPreOrder ? (
            <>
              <AddToCart
                price={variant.shopify.price}
                compareAtPrice={variant.shopify.compareAtPrice}
                inventoryQuantity={inventoryQuantity}
                inventoryData={useInventoryData}
                sku={variant.sku}
                shopifyVariantId={variant.shopify.shopifyId}
                productName={variant.productName}
                variantName={variant.variantName}
                slug={variant.slug}
                mtoMoq={variant.airtable.mtoMoq}
              />
              <VariantAvailability
                inventoryData={useInventoryData}
                placement="product"
                sku={variant.sku}
                isCustom={
                  (variant.airtable.type || '').toLowerCase() === 'custom'
                }
              />
            </>
          ) : (
            (() => {
              switch (variant.airtable.status) {
                case AirtableStatus.ACTIVE: {
                  return (
                    <>
                      <BackInStock
                        name={`${variant.productName} ${variant.variantName}`}
                        shopifyVariantId={variant.shopify.shopifyId.replace(
                          'gid://shopify/ProductVariant/',
                          '',
                        )}
                        sku={variant.sku}
                      />
                    </>
                  );
                }
                case AirtableStatus.PRE_ORDER: {
                  return (
                    <>
                      <Info
                        type="green"
                        title="Need a quote?"
                        content={
                          <p className="max-w-prose text-pretty text-base text-black-600">
                            Send an email to{' '}
                            <a href="mailto:orders@hem.com">orders@hem.com</a>{' '}
                            and we'll be happy to assist you with quantities and
                            specific delivery dates.
                          </p>
                        }
                      />
                      <Modal
                        title="Add to waitlist"
                        trigger={
                          <Button className="w-full" color="green-matte">
                            Add to waitlist
                          </Button>
                        }
                        content={
                          <div className="p-4">
                            <MarketingCommunicationForms
                              form="newsletter"
                              newsletterContent="As an exclusive preview for our Hem Pro members, you can browse our upcoming arrivals before they hit the shelves. Sign up for our newsletter to be the first to shop."
                              tags={[
                                `pre-order-lead-${new Date().getFullYear()}`,
                                `pre-order-${variant.productName}-lead-${new Date().getFullYear()}`.toLowerCase(),
                              ]}
                            />
                          </div>
                        }
                      />
                    </>
                  );
                }
                default: {
                  return (
                    <Button className="w-full" color="black" disabled>
                      Sold out
                    </Button>
                  );
                }
              }
            })()
          )}

          {variant.productName !== 'Alphabeta' &&
            variant.productName !== 'Toto' &&
            (variantPrimaryOptions.length > 1 ||
              variantSecondaryOptions.length > 1 ||
              variantConfigurationOptions.length > 1) && (
              <div className="mt-4 divide-y divide-black-100 border-t pt-4">
                {variantPrimaryOptions.length > 1 && (
                  <div className="mb-4">
                    <div className="mb-4 flex justify-between gap-2 text-base">
                      <p className="font-bold">
                        Select{' '}
                        {(
                          variant.airtable.primaryMaterialFor ||
                          'Primary finish'
                        ).toLowerCase()}
                      </p>

                      {IS_PRO &&
                        SHOW_MTO_PROGRAM_PDF_FOR_FAMILIES.includes(
                          variant.productName,
                        ) ? (
                        <Modal
                          trigger={
                            <button className="link-secondary flex items-center gap-2">
                              Graded-in textiles <InfoIcon size={14} />
                            </button>
                          }
                          title="Graded-in textiles"
                          content={
                            <div className="text-pretty p-4 text-base text-black-600">
                              <p className="mb-2">
                                To complement our existing in-stock collection
                                Hem offers a made-to-order program which makes
                                it easy for partners, designers, and architects
                                to satisfy their individual needs and
                                preferences.
                              </p>
                              <p className="mb-2">
                                See the below guide for the feasibility of each
                                textiles for each specific product family.
                              </p>
                              <p>
                                Please reach out to{' '}
                                <a
                                  className="link-primary"
                                  href="mailto:orders@hem.com"
                                >
                                  orders@hem.com
                                </a>{' '}
                                for any questions or requests.
                              </p>
                              <div className="-mx-4 -mb-4 mt-4 border-t">
                                <a
                                  className="group grid grid-cols-[auto,auto,1fr,auto] items-center gap-x-2 px-4 py-4 hover:bg-black-50 hover:no-underline"
                                  href={
                                    customer !== undefined
                                      ? 'https://go.hem.com/made-to-order/pre-order-graded-in-textiles'
                                      : 'https://go.hem.com/made-to-order/graded-in-textiles'
                                  }
                                  download=""
                                >
                                  <Paperclip
                                    size={20}
                                    className="text-black-400"
                                  />
                                  <span className="line-clamp-1 text-base font-bold text-black">
                                    Graded-in textiles
                                  </span>
                                  <span className="text-base text-black-400">
                                    PDF
                                  </span>
                                  <span className="text-base font-bold text-blue group-hover:text-black">
                                    Download
                                  </span>
                                </a>
                              </div>
                            </div>
                          }
                        />
                      ) : null}
                    </div>

                    <ul className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] items-start gap-4">
                      {variantPrimaryOptions
                        .sort((a, b) => {
                          let aTitle = a.airtable.primaryMaterial?.finishTitle;
                          let bTitle = b.airtable.primaryMaterial?.finishTitle;

                          if (
                            variant.productName === 'Hide' &&
                            aTitle === 'Signal Black'
                          ) {
                            aTitle = 'Jet Black';
                          }

                          if (
                            variant.productName === 'Hide' &&
                            bTitle === 'Signal Black'
                          ) {
                            bTitle = 'Jet Black';
                          }

                          if (aTitle < bTitle) {
                            return -1;
                          } else if (aTitle > bTitle) {
                            return 1;
                          }
                          // a must be equal to b
                          return 0;
                        })
                        .map((option) => (
                          <Material
                            key={option.sku}
                            slug={option.slug}
                            isCurrent={option.sku === variant.sku}
                            title={option.airtable.primaryMaterial?.finishTitle}
                            image={
                              option.airtable.primaryMaterial.image
                                ?.gatsbyImageData
                            }
                          />
                        ))}
                    </ul>

                    {variantPrimaryCustomOptions.length > 0 && (
                      <React.Suspense fallback={null}>
                        <CustomOptions
                          options={variantPrimaryCustomOptions}
                          currentVariantSku={variant.sku}
                        />
                      </React.Suspense>
                    )}
                  </div>
                )}

                {variant.productName === 'Hide' && bothTextures.length > 1 && (
                  <React.Suspense fallback={null}>
                    <SelectTexture
                      bothTextures={bothTextures}
                      variant={variant}
                    />
                  </React.Suspense>
                )}

                {variantSecondaryOptions.length > 1 && (
                  <div className="mb-4">
                    <p className="my-4 text-base font-bold">
                      Select{' '}
                      {(
                        variant.airtable.secondaryMaterialFor ||
                        'Secondary finish'
                      ).toLowerCase()}
                    </p>
                    <ul className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] items-start gap-4">
                      {sortBy(
                        variantSecondaryOptions,
                        // prettier-ignore
                        (option) => option.airtable.secondaryMaterial?.finishTitle,
                      ).map((option) => (
                        <Material
                          key={option.sku}
                          slug={option.slug}
                          isCurrent={option.sku === variant.sku}
                          title={option.airtable.secondaryMaterial?.finishTitle}
                          image={
                            option.airtable.secondaryMaterial.image
                              ?.gatsbyImageData
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}

                {variantConfigurationOptions.length > 1 && (
                  <div className="mb-4">
                    <p className="my-4 text-base font-bold">
                      Select configuration
                    </p>
                    <ul className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] items-start gap-4">
                      {sortBy(
                        variantConfigurationOptions,
                        (option) => option.airtable.product.configuration,
                      )
                        .filter((option) => {
                          if (variant.productName !== 'Hide') {
                            return true;
                          }

                          return isTextured
                            ? option.airtable.primaryMaterial?.texture ===
                            'Fine'
                            : option.airtable.primaryMaterial?.texture ===
                            'None';
                        })
                        .map((option) => (
                          <Material
                            key={option.sku}
                            slug={option.slug}
                            isCurrent={option.sku === variant.sku}
                            title={option.airtable.product.configuration}
                            image={option.image?.gatsbyImageData}
                          />
                        ))}
                    </ul>
                  </div>
                )}

                {variant.productName === 'Touchwood' &&
                  variant.airtable.product.configuration !==
                  'Chair (Wooden legs)' && (
                    <React.Suspense fallback={null}>
                      <TouchwoodWoodPush />
                    </React.Suspense>
                  )}
              </div>
            )}

          {pairsWith.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <p className="mb-4 text-base font-bold">Pairs with</p>
              <ul className="mb-4 grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] items-start gap-4">
                {sortBy(
                  pairsWith.filter(
                    (variant) =>
                      variant?.airtable?.product?.configuration !== undefined,
                  ),
                  // prettier-ignore
                  (option) => `${option.productName} ${option.airtable.product.configuration}`,
                ).map((option) => (
                  <Material
                    key={option.slug}
                    slug={option.slug}
                    isCurrent={false}
                    title={`${option.productName} ${option.airtable.product.configuration}`}
                    image={option.image?.gatsbyImageData}
                  />
                ))}
              </ul>
            </div>
          )}

          {variant.productName === 'Toto' && (
            <Suspense fallback={<LoadingIndicator />}>
              <TotoConfiguration currentSku={variant.sku} />
            </Suspense>
          )}

          {variant.productName === 'Alphabeta' && (
            <Button
              to="/lighting/alphabeta"
              color="white-with-blue-border"
              className="mt-4 flex w-full justify-center gap-2"
            >
              <DashboardCustomizeOutlinedIcon fontSize="small" />
              Configure your own Alphabeta
            </Button>
          )}
        </div>
      </div>

      {Array.isArray(variant.lifestyleImages?.images) && (
        <LifestyleGallery images={variant.lifestyleImages.images} />
      )}

      <div className="mx-4 mb-16 gap-2 md:mx-8 md:grid md:grid-cols-12 md:gap-4">
        <section
          className={cn({
            'md:col-span-8': !!variant.product.relatedArticle,
            'md:col-span-full': !variant.product.relatedArticle,
          })}
        >
          <SectionTitle title="Technical details" titleTag="h3" border />
          <div
            className={cn('grid gap-2 pt-2 md:gap-4', {
              'md:grid-cols-2': !!variant.product.relatedArticle,
              'md:grid-cols-3 ml:grid-cols-4': !variant.product.relatedArticle,
            })}
          >
            <TechnicalDetails variant={variant} />
          </div>
        </section>

        {!!variant.product.relatedArticle && (
          <div className="pt-8 md:col-span-4 md:pt-11">
            {(() => {
              switch (variant.product.relatedArticle?.__typename) {
                case 'ContentfulPageDesigner':
                  return (
                    <ArticlePreview
                      titleTag="h3"
                      titleVariant={TextVariant.TITLE_SMALL}
                      title={`Design by ${variant.product.relatedArticle.name}`}
                      excerpt={variant.product.relatedArticle.description}
                      featuredImage={
                        variant.product.relatedArticle.previewImage
                      }
                      slug={variant.product.relatedArticle.slug}
                    />
                  );

                case 'ContentfulPageArticle':
                  return (
                    <ArticlePreview
                      titleTag="h3"
                      titleVariant={TextVariant.TITLE_SMALL}
                      title={variant.product.relatedArticle.title}
                      excerpt={variant.product.relatedArticle.excerpt}
                      slug={variant.product.relatedArticle.slug}
                      featuredImage={
                        variant.product.relatedArticle.featuredImage
                      }
                    />
                  );

                case 'ContentfulPage':
                  return (
                    <ArticlePreview
                      titleTag="h3"
                      titleVariant={TextVariant.TITLE_SMALL}
                      title={
                        variant.product.relatedArticle.metaContent?.metaTitle
                      }
                      excerpt={{
                        childMarkdownRemark: {
                          excerpt:
                            variant.product.relatedArticle.metaContent
                              ?.metaDescription,
                        },
                      }}
                      slug={variant.product.relatedArticle.slug}
                      featuredImage={
                        variant.product.relatedArticle.metaContent?.ogImage
                      }
                    />
                  );

                default:
                  return null;
              }
            })()}
          </div>
        )}
      </div>

      {['variant', 'custom', 'part'].includes(
        (variant.airtable.type || '').toLowerCase(),
      ) && (
          <Slice
            alias="downloads"
            showGradedInDownloadOption={
              IS_PRO &&
              SHOW_MTO_PROGRAM_PDF_FOR_FAMILIES.includes(variant.productName)
            }
            type={variant.airtable.type}
            allowEmpty
          />
        )}

      {['variant', 'custom'].includes(
        (variant.airtable.type || '').toLowerCase(),
      ) && (
          <Slice
            alias="ugc"
            configuration={variant.airtable.product.configuration}
            allowEmpty
          />
        )}

      {['variant', 'custom'].includes(
        (variant.airtable.type || '').toLowerCase(),
      ) && (
          <ReviewsContainer
            reviews={reviews.nodes}
            title={variant.productName}
            configuration={variant.airtable.product.configuration}
          />
        )}

      {['variant', 'custom'].includes(
        (variant.airtable.type || '').toLowerCase(),
      ) &&
        !isPreOrder && (
          <ProductRecommendations
            categoriesLvl0={variant.subcategory?.category?.department?.title}
            categoriesLvl1={variant.subcategory?.category?.title}
            categoriesLvl2={variant.subcategory?.title}
            sku={variant.sku}
            productName={variant.productName}
          />
        )}
    </>
  );
};

const VariantPageWrapper: React.FC<
  PageProps & {
    data: Queries.VariantPageQuery;
  }
> = ({ location, data }): JSX.Element => {
  const [hasMounted, setHasMounted] = useState<boolean>(false);

  const shouldShowPreOrder = useShowPreOrderVariants();
  const isPreOrderVariant =
    data.variant.airtable.status === AirtableStatus.PRE_ORDER;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (isPreOrderVariant) {
      // Define the scroll handler inside the useEffect hook.
      const scrollHandler = () => {
        sessionStorage.setItem('pre-order-scroll', window.scrollY.toString());
      };

      window.addEventListener('scroll', scrollHandler);

      // Remove the scroll event listener when the component unmounts or the variant changes.
      return () => {
        window.removeEventListener('scroll', scrollHandler);
      };
    } else {
      sessionStorage.removeItem('pre-order-scroll');
    }
  }, [isPreOrderVariant]);

  useEffect(() => {
    // If the component is mounted, the variant is a pre-order, and it should be shown,
    // restore the scroll position and remove the saved value.
    if (
      hasMounted &&
      shouldShowPreOrder &&
      isPreOrderVariant &&
      sessionStorage.getItem('pre-order-scroll')
    ) {
      window.scrollTo(
        0,
        parseInt(sessionStorage.getItem('pre-order-scroll') || '0'),
      );
      sessionStorage.removeItem('pre-order-scroll');
    }
  }, [hasMounted, isPreOrderVariant, shouldShowPreOrder]);

  if (isPreOrderVariant) {
    // If React hasn't mounted yet, we can't know if the customer is logged in
    if (!hasMounted) {
      return <PreOrderLoading />;
    }

    // If shouldShowPreOrder === false, most likely the customer is not logged in
    // so we render a message telling the customer to log in if they want to view the product
    if (isPreOrderVariant && !shouldShowPreOrder) {
      return <PreOrderLogIn location={location} />;
    }
  }

  return <VariantPage data={data} />;
};

export default VariantPageWrapper;

export const Head = ({
  data: { variant, reviews },
}: {
  data: Queries.VariantPageQuery;
}) => {
  const isPreSale = variant.airtable.status === AirtableStatus.PRE_ORDER;
  if (isPreSale) {
    return null;
  }

  const firstImageInArray = first(variant.slideshow);
  const banner = firstImageInArray?.url;

  // WE ONLY DO THIS FOR US/EU B2C
  let shipping = undefined;
  if (
    process.env.GATSBY_REGION_REGION_DIFFERENTIATOR === 'EU B2C' ||
    process.env.GATSBY_REGION_REGION_DIFFERENTIATOR === 'UK B2C' ||
    process.env.GATSBY_REGION_REGION_DIFFERENTIATOR === 'US B2C'
  ) {
    // B2C Shipping
    // Small: $9 / €9
    // Medium: $19 / €19
    // Large: $49 / €49
    // X-Large: $149 / €149
    switch (variant.airtable.shipsAs) {
      case 'Small':
        shipping = 9;
        break;
      case 'Medium':
        shipping = 19;
        break;
      case 'Large':
        shipping = 49;
        break;
      case 'X-Large':
        shipping = 149;
        break;
    }
  }

  return (
    <SEO
      title={`${variant.productName} ${variant.variantName}`}
      desc={variant.airtable?.product?.description}
      banner={banner}
      type="product"
      price={variant.shopify.price + ''}
      reviews={reviews.nodes}
      inStock={variant.shopify.inventoryQuantity > 0}
      shipping={shipping}
      productName={variant.productName}
      productImages={variant.slideshow}
      crumbs={getBreadcrumbs(variant)}
    />
  );
};

export const query = graphql`
  query VariantPage($id: String!, $sku: String!) {
    reviews: allContentfulYotpoProductReview(
      filter: { skus: { in: [$sku] } }
      sort: { yotpoCreatedAt: DESC }
    ) {
      nodes {
        ...ProductReview
      }
    }

    variant: contentfulProductVariant(id: { eq: $id }) {
      sku
      slug
      productName
      variantName
      cylindo
      cylindoCode

      thumbnailImage {
        url
      }

      subcategory: primarySubcategory {
        title
        slug

        category {
          title
          slug

          department {
            title
            slug
          }
        }
      }

      airtable {
        primaryMaterialFor
        secondaryMaterialFor
        type
        status
        shipsAs
        approxLaunchDate
        mtoMoq

        primaryMaterial {
          cylindoId
          ...MaterialTechnicalDetails

          consistsOf {
            ...MaterialTechnicalDetails
          }
        }

        secondaryMaterial {
          cylindoId
          ...MaterialTechnicalDetails

          consistsOf {
            ...MaterialTechnicalDetails
          }
        }

        product {
          designer
          description
          configuration
          warranty
        }
      }

      slideshow: images {
        url
        gatsbyImageData(
          formats: [AUTO, AVIF, WEBP]
          quality: 80
          width: 1136
          height: 757
          placeholder: NONE
        )
      }

      slideshowThumbnails: images {
        url
        gatsbyImageData(
          formats: [AUTO, AVIF, WEBP]
          quality: 80
          width: 63
          height: 42
          layout: FIXED
          placeholder: NONE
        )
      }

      product {
        relatedArticle {
          ... on ContentfulPageDesigner {
            __typename
            name
            slug
            description {
              childMarkdownRemark {
                excerpt
              }
            }

            previewImage {
              gatsbyImageData(
                formats: [AUTO, AVIF, WEBP]
                quality: 80
                width: 920
                aspectRatio: 1.5
                resizingBehavior: FILL
                cropFocus: FACES
                placeholder: BLURRED
              )
            }
          }
          ... on ContentfulPageArticle {
            __typename
            ...ArticlePreview
          }
          ... on ContentfulPage {
            __typename
            slug
            metaContent {
              metaTitle
              metaDescription
              ogImage {
                gatsbyImageData(
                  formats: [AUTO, AVIF, WEBP]
                  quality: 80
                  width: 920
                  aspectRatio: 1.5
                  resizingBehavior: FILL
                  cropFocus: FACES
                  placeholder: BLURRED
                )
              }
            }
          }
        }
      }

      shopify {
        shopifyId
        price
        compareAtPrice
        inventoryQuantity
        product {
          id
          status
          shopifyId
        }
      }

      lifestyleImages {
        images {
          gatsbyImageData(
            formats: [AUTO, AVIF, WEBP]
            quality: 80
            width: 960
            aspectRatio: 1.5
          )
          description
          metadata {
            tags {
              contentful_id
            }
          }
          file {
            contentType
            url
          }
        }
      }

      primaryOptions {
        sku
        slug
        airtable {
          primaryMaterial {
            finishTitle
            texture
            image {
              gatsbyImageData(
                formats: [AUTO, AVIF, WEBP]
                quality: 80
                layout: FIXED
                width: 36
                height: 36
                placeholder: BLURRED
                resizingBehavior: THUMB
              )
            }
          }
        }
      }

      primaryCustomOptions {
        sku
        slug
        airtable {
          primaryMaterial {
            finishTitle
            texture
            image {
              gatsbyImageData(
                formats: [AUTO, AVIF, WEBP]
                quality: 80
                layout: FIXED
                width: 36
                height: 36
                placeholder: BLURRED
                resizingBehavior: THUMB
              )
            }
          }
        }
      }

      secondaryOptions {
        sku
        slug
        airtable {
          secondaryMaterial {
            finishTitle
            image {
              gatsbyImageData(
                formats: [AUTO, AVIF, WEBP]
                quality: 80
                layout: FIXED
                width: 36
                height: 36
                placeholder: BLURRED
                resizingBehavior: THUMB
              )
            }
          }
        }
      }

      configurationOptions {
        sku
        slug
        airtable {
          primaryMaterial {
            texture
          }
          product {
            configuration
          }
        }
        image: thumbnailImage {
          gatsbyImageData(
            formats: [AUTO, AVIF, WEBP]
            quality: 80
            layout: FIXED
            width: 60
            height: 60
            placeholder: NONE
            aspectRatio: 1
          )
        }
      }

      pairsWith {
        slug
        productName
        airtable {
          sellToCountries
          product {
            configuration
          }
        }
        image: thumbnailImage {
          gatsbyImageData(
            formats: [AUTO, AVIF, WEBP]
            quality: 80
            layout: FIXED
            width: 60
            height: 60
            placeholder: NONE
            aspectRatio: 1
          )
        }
      }

      techinalDetails: airtable {
        certifications {
          category
          shortTitle
          slug
        }
        capacity: capacityMl
        forestryCertification
        insideMaterial
        countryOfOrigin
        width: widthCm
        depth: depthCm
        length: lengthCm
        heightCm
        heightIn
        diameter: diameterCm
        armrestHeight: armrestHeightCm
        backrestHeightCm
        backrestHeightIn
        seatWidth: seatWidthCm
        seatDepth: seatDepthCm
        seatHeightCm
        seatHeightIn
        pileHeight: pileHeightCm
        cableLength: cableLengthCm
        lightbulb
        dimmable
        weight: netWeightKg
        boxes
        packageVolume: packageVolumeM
        grossWeight: grossWeightKg
        packageWidth: packageWidthCm
        packageLength: packageLengthCm
        packageHeight: packageHeightCm
        shelfDepth: shelfDepthCm
        shelfWidth: shelfWidthCm
        stackabilityMaxQty
        stackabilityMaxHeight: stackabilityMaxHeightCm
        additionalNotesExternal
      }
    }
  }
`;
